import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { useAuth } from '../context/AuthContext';
import { createLogger } from '../utils/logger';

const logger = createLogger({ module: 'usePerformanceReviews' });

export interface PerformanceReviewDoc {
  id: string;
  user_id: string;
  review_name: string;
  review_text: string;
  context_counts: any;
  review_date?: string;
  review_goal?: string;
  output_style?: string;
  is_pinned: boolean;
  created_at: string;
  updated_at?: string;
  // Additional metadata from review_preparation
  preparation_data?: {
    selectedAchievements?: any[];
    keyMetrics?: any[];
    talkingPoints?: any;
    executiveSummary?: string;
  };
}

interface UsePerformanceReviewsReturn {
  reviews: PerformanceReviewDoc[];
  loading: boolean;
  error: string | null;
  refreshReviews: () => Promise<void>;
  deleteReview: (reviewId: string) => Promise<boolean>;
}

export const usePerformanceReviews = (focusId?: string | null): UsePerformanceReviewsReturn => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<PerformanceReviewDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch reviews for this performance review focus
  const fetchReviews = useCallback(async () => {
    if (!user || !focusId) {
      setReviews([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // First, get review preparations for this focus
      const { data: preparations, error: prepError } = await supabase
        .from('review_preparation')
        .select('*')
        .eq('focus_id', focusId)
        .eq('preparation_type', 'performance_review');

      if (prepError) {
        logger.error('Failed to fetch review preparations', { error: prepError });
      }

      // Then fetch the actual reviews
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select('*')
        .eq('user_id', user.id)
        .eq('review_goal', 'Performance Review Preparation')
        .order('created_at', { ascending: false });

      if (reviewsError) throw reviewsError;

      // Merge preparation data with reviews
      const mergedReviews = reviewsData?.map(review => {
        const preparation = preparations?.find(prep => {
          // Match by creation time (within 1 minute) since we create them together
          const reviewTime = new Date(review.created_at).getTime();
          const prepTime = new Date(prep.created_at).getTime();
          return Math.abs(reviewTime - prepTime) < 60000; // 1 minute
        });

        return {
          ...review,
          preparation_data: preparation?.content || null
        };
      }) || [];

      setReviews(mergedReviews);
    } catch (err) {
      logger.error('Failed to fetch performance reviews', { error: err });
      setError('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [user, focusId]);

  // Delete a review
  const deleteReview = useCallback(async (reviewId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', reviewId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Update local state
      setReviews(prev => prev.filter(r => r.id !== reviewId));
      return true;
    } catch (err) {
      logger.error('Failed to delete review', { error: err });
      setError('Failed to delete review');
      return false;
    }
  }, [user]);

  // Refresh function
  const refreshReviews = useCallback(() => {
    return fetchReviews();
  }, [fetchReviews]);

  // Initial fetch
  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  return {
    reviews,
    loading,
    error,
    refreshReviews,
    deleteReview
  };
};